'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Readiness signals tracked by the app.
 * Components signal ready when they've finished their initial load.
 */
type ReadySignal = 'auth' | 'organizations' | 'layout' | 'nav' | 'sidebar' | 'content';

interface AppReadyContextValue {
  /** Signal that a component is ready */
  signalReady: (signal: ReadySignal) => void;
  /** Register that a signal is expected (should be waited for) */
  expectSignal: (signal: ReadySignal) => void;
  /** Check if all required signals are ready */
  isAppReady: boolean;
  /** Reset all signals (used on navigation) */
  resetSignals: () => void;
  /** Check if a specific signal is ready */
  isSignalReady: (signal: ReadySignal) => boolean;
}

const AppReadyContext = createContext<AppReadyContextValue | null>(null);

/**
 * Core signals that are always required.
 * - auth: Session status resolved (authenticated or unauthenticated)
 * - organizations: User's organizations have been fetched
 * - layout: ContentGate/AppShellContent has mounted
 * - nav: AppNav has rendered
 */
const CORE_REQUIRED_SIGNALS: ReadySignal[] = ['auth', 'organizations', 'layout', 'nav'];

// Safety net: never allow the app to be stuck behind the initial loader forever.
// If critical signals don't resolve (network/auth/hydration edge cases), show the shell anyway.
const MAX_INITIAL_APP_READY_WAIT_MS = 10_000;

interface AppReadyProviderProps {
  children: ReactNode;
}

export function AppReadyProvider({ children }: AppReadyProviderProps) {
  const [readySignals, setReadySignals] = useState<Set<ReadySignal>>(new Set());
  const [expectedSignals, setExpectedSignals] = useState<Set<ReadySignal>>(new Set(CORE_REQUIRED_SIGNALS));
  // Track if we've already shown the app once (don't re-show loader on nav)
  const [hasShownOnce, setHasShownOnce] = useState(false);

  const signalReady = useCallback((signal: ReadySignal) => {
    setReadySignals((prev) => {
      if (prev.has(signal)) return prev;
      const next = new Set(prev);
      next.add(signal);
      return next;
    });
  }, []);

  const expectSignal = useCallback(
    (signal: ReadySignal) => {
      if (hasShownOnce) return;

      setExpectedSignals((prev) => {
        if (prev.has(signal)) return prev;
        const next = new Set(prev);
        next.add(signal);
        return next;
      });
    },
    [hasShownOnce]
  );

  const resetSignals = useCallback(() => {
    if (!hasShownOnce) {
      setReadySignals(new Set());
      setExpectedSignals(new Set(CORE_REQUIRED_SIGNALS));
    }
  }, [hasShownOnce]);

  const isSignalReady = useCallback(
    (signal: ReadySignal) => readySignals.has(signal),
    [readySignals]
  );

  const allExpectedReady = useMemo(
    () => Array.from(expectedSignals).every((signal) => readySignals.has(signal)),
    [expectedSignals, readySignals]
  );

  const isAppReady = hasShownOnce || allExpectedReady;

  // Latch readiness once everything expected is ready.
  useEffect(() => {
    if (allExpectedReady) {
      setHasShownOnce(true);
    }
  }, [allExpectedReady]);

  // Failsafe timeout: prevent indefinite "Loading" overlay on initial load.
  useEffect(() => {
    if (hasShownOnce || allExpectedReady) return;
    const timer = setTimeout(() => {
      setHasShownOnce(true);
    }, MAX_INITIAL_APP_READY_WAIT_MS);
    return () => clearTimeout(timer);
  }, [allExpectedReady, hasShownOnce]);

  return (
    <AppReadyContext.Provider value={{ signalReady, expectSignal, isAppReady, resetSignals, isSignalReady }}>
      {children}
    </AppReadyContext.Provider>
  );
}

/**
 * Hook to access the app ready context.
 * Returns null if used outside the provider (graceful degradation).
 */
export function useAppReadyOptional(): AppReadyContextValue | null {
  return useContext(AppReadyContext);
}

/**
 * Hook to access the app ready context.
 * Throws if used outside the provider.
 */
export function useAppReady(): AppReadyContextValue {
  const context = useContext(AppReadyContext);
  if (!context) {
    throw new Error('useAppReady must be used within an AppReadyProvider');
  }
  return context;
}
